/**
 * Regras puras da grade de participantes remotos (sala com várias pessoas, plano do Otto): ordem estável, páginas,
 * colunas e tamanho dos tiles. Sem React, para testar sem tela.
 *
 * Uma sala de 1:1 (um participante remoto) NÃO usa a grade: o tile ocupa o palco como sempre (ver `RemoteGrid`).
 */

/** Tiles por página: a janela flutuante e o celular têm pouco espaço; a sala ampliada e o convidado no computador, 3x3. */
export const PAGE_SIZE = Object.freeze({ compact: 4, roomy: 9 });

/** Participantes remotos por página. @param {boolean} compactLayout janela flutuante/compacta ou celular */
export function pageSizeFor(compactLayout) {
    return compactLayout ? PAGE_SIZE.compact : PAGE_SIZE.roomy;
}

/**
 * Ordem ESTÁVEL: por hora de entrada na sala (depois pela identidade). Quem entra vai para o fim e ninguém muda de lugar
 * quando alguém liga a câmera ou fala (o LiveKit reordena por isso, e a grade pularia o tempo todo).
 * @template {{ participant: { identity?: string, joinedAt?: Date|number|null } }} T
 * @param {T[]} tracks
 * @returns {T[]}
 */
export function orderRemoteTracks(tracks) {
    const joinedMs = (t) => {
        const at = t.participant?.joinedAt;
        const ms = at instanceof Date ? at.getTime() : Number(at);
        return Number.isFinite(ms) ? ms : 0;
    };
    return [...tracks].sort((a, b) => joinedMs(a) - joinedMs(b)
        || String(a.participant?.identity ?? '').localeCompare(String(b.participant?.identity ?? '')));
}

/**
 * Uma página da lista. A página pedida é limitada ao que existe (alguém saiu e a última página sumiu).
 * @template T
 * @param {T[]} items
 * @param {number} page começa em 0
 * @param {number} pageSize
 * @returns {{ items: T[], page: number, pages: number, total: number }}
 */
export function paginate(items, page, pageSize) {
    const total = items.length;
    const pages = Math.max(1, Math.ceil(total / pageSize));
    const safe = Math.min(Math.max(0, page), pages - 1);
    return { items: items.slice(safe * pageSize, (safe + 1) * pageSize), page: safe, pages, total };
}

/**
 * Colunas da grade para `count` tiles na página. Larga: o quadrado mais próximo (2 -> 2, 3..4 -> 2, 5..9 -> 3). Compacta:
 * no máximo 2, e dois tiles ficam um sobre o outro (a janela é estreita).
 * @param {number} count
 * @param {boolean} compactLayout
 */
export function columnsFor(count, compactLayout) {
    if (count <= 1) return 1;
    const natural = Math.ceil(Math.sqrt(count));
    if (!compactLayout) return natural;
    return count === 2 ? 1 : Math.min(2, natural);
}

/** Tamanho do avatar/nome do tile: quanto mais gente na página, menor. @returns {'md'|'sm'|'xs'} */
export function tileSizeFor(count, compactLayout) {
    if (compactLayout) return count <= 2 ? 'sm' : 'xs';
    return count <= 4 ? 'md' : 'sm';
}
